import type { BaseAPIResponse, Meta } from '~/types/api';
import type { PartnerCompany } from '~/types/business';
import { pbRequest, pbRequestRaw, pbRequestVoid } from '~/services/request';

// Partner surface — sub-company roster, partner analytics, and license
// provisioning.
export const partnerService = {
    /**
     * One page of the companies this partner manages.
     */
    async getPartnerCompanies(
        companyId: number,
        params?: { search?: string; per_page?: number; page?: number }
    ): Promise<{ data: PartnerCompany[]; meta: Meta | undefined }> {
        const envelope = await pbRequestRaw<BaseAPIResponse<PartnerCompany[]>>(`/companies/${companyId}/partner/companies`, {
            method: 'POST',
            body: {
                filters: { search: params?.search || '' },
                per_page: params?.per_page || 10,
                page: params?.page || 1,
            },
            failure: 'Failed to fetch partner companies',
        });

        // The page's meta rides along: partner/companies.vue pages its table by meta.total.
        return { data: envelope.data, meta: envelope.meta };
    },

    /**
     * Get partner revenue statistics
     */
    async getPartnerRevenueStats(companyId: number): Promise<any[]> {
        return await pbRequest<any[]>(`/companies/${companyId}/partner/analytics/revenue`, {
            failure: 'Failed to fetch partner revenue stats',
        });
    },

    /**
     * Get partner business statistics
     */
    async getPartnerBusinessStats(companyId: number): Promise<any[]> {
        return await pbRequest<any[]>(`/companies/${companyId}/partner/analytics/business`, {
            failure: 'Failed to fetch partner business stats',
        });
    },

    /**
     * Get all license types available for provisioning
     * Partners can provision any of these license types in any quantity
     */
    async getLicenseTypes(
        companyId: number
    ): Promise<Array<{ id: number; slug: string; name: string; available: number; pool: number }>> {
        const envelope = await pbRequestRaw<BaseAPIResponse<any[]>>(`/companies/${companyId}/partner/license-types`, {
            failure: 'Failed to fetch license types',
        });

        // Map response to consistent format
        return envelope.data.map((l: any) => ({
            id: l.id,
            slug: l.slug || l.id,
            name: l.name,
            available: l.available || 0,
            pool: l.pool || 0,
        }));
    },

    /**
     * Provision licenses to a company
     * Used by partners to allocate licenses to themselves or sub-companies
     */
    async provisionLicenses(
        sourceCompanyId: number,
        targetCompanyId: number,
        licenses: Array<{ license_id: number; quantity: number }>,
        idempotencyKey: string
    ): Promise<void> {
        await pbRequestVoid(`/companies/${sourceCompanyId}/partner/provision-licenses`, {
            method: 'POST',
            headers: { 'Idempotency-Key': idempotencyKey },
            body: {
                target_company_id: targetCompanyId,
                licenses,
            },
            expect: [200, 201, 204],
            failure: 'Failed to provision licenses',
        });
    },

    /**
     * Get provisioned licenses history
     */
    async getProvisionedLicenses(companyId: number): Promise<any[]> {
        return await pbRequest<any[]>(`/companies/${companyId}/partner/provisioned-licenses`, {
            failure: 'Failed to fetch provisioned licenses',
        });
    },
};
